import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import {
  CheckCircle2,
  Sparkles,
  Calendar,
  ArrowRight,
  Loader2,
  XCircle,
  Home
} from 'lucide-react';

const ElectiveReservationSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/academy/electives/status/${sessionId}`);
        
        if (response.data.success) {
          setStatus(response.data.payment_status);
          setReservationDetails({
            electiveTitle: response.data.elective_title,
            amount: response.data.amount,
            email: response.data.customer_email
          });
        } else {
          setError('Failed to verify payment');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError(err.response?.data?.detail || 'Failed to verify payment status');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Verifying your reservation...</p>
        </div>
      </div>
    );
  }

  if (error || status !== 'paid') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-20 px-4">
        <Card className="bg-gray-900 border-red-500/50 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-black text-white mb-4">Payment Issue</h1>
            <p className="text-gray-400 mb-6">
              {error || 'Your payment could not be verified. Please try again or contact support.'}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/academy/electives')}
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold w-full"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/academy')}
                variant="outline"
                className="border-gray-700 text-gray-400 hover:bg-gray-800 w-full"
              >
                Back to Academy
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Celebration Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-12">
        {/* Success Card */}
        <Card className="bg-gradient-to-br from-gray-900 to-gray-900/50 border-2 border-orange-500/50 overflow-hidden">
          <CardContent className="p-8 text-center">
            {/* Success Icon */}
            <div className="relative mb-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <div className="absolute -top-2 -right-2 left-0 right-0 flex justify-center">
                <Sparkles className="w-6 h-6 text-orange-500 animate-pulse" />
              </div>
            </div>

            {/* Title */}
            <Badge className="bg-green-500/20 text-green-500 border border-green-500/50 mb-4">
              SEAT RESERVED
            </Badge>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
              You're In!
            </h1>

            {/* Reservation Details */}
            {reservationDetails && (
              <div className="bg-gray-800/50 rounded-xl p-6 mb-8 text-left">
                <h3 className="text-orange-500 font-bold text-sm uppercase tracking-wider mb-4">
                  Reservation Details
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Elective</span>
                    <span className="text-white font-bold">{reservationDetails.electiveTitle}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Deposit Paid</span>
                    <span className="text-green-500 font-bold">${reservationDetails.amount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Launch</span>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-orange-500" />
                      <span className="text-white font-bold">Spring '26</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* What's Next */}
            <div className="text-left mb-8">
              <h3 className="text-white font-bold mb-3">What's Next?</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span>A confirmation email has been sent to your inbox</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Your elective will appear in your "Minor Focus" section when Spring '26 launches</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span>You'll receive early access notifications and prep materials</span>
                </li>
              </ul>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/academy/electives')}
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold flex-1"
                data-testid="reserve-another-btn"
              >
                Reserve Another Elective
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/academy')}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800 flex-1"
                data-testid="back-to-academy-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Academy
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Questions about your reservation? Contact us at{' '}
          <a href="mailto:academy@prsvr.com" className="text-orange-500 hover:underline">
            academy@prsvr.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default ElectiveReservationSuccess;
